/**
 * @file stage-list-types.ts
 * @description Общее описание списка стадий для настроек «Стадии заказов» и
 * «Стадии возвратов» (phase10.5; phase19.31 — один редактор на оба списка).
 *
 * Страницы различаются только данными: откуда грузить, как сохранить
 * порядок, как удалить, какое окно правки открыть и как назвать единицы
 * («заказов» / «возвратов»). Это и есть `StageListConfig` — новый вид
 * стадий (например, сделки на этапе 2) = ещё одна запись, а не третья копия
 * страницы.
 */

import type { ReactNode } from "react";

import { pluralRu } from "../../../lib/plural";

/** Минимум, который редактор знает о стадии. */
export interface StageListItem {
  id: string;
  name: string;
  /** Цвет метки магазина — только точкой (SC28). */
  color: string | null;
  slaHours: number | null;
  isActive: boolean;
}

/** Что открыть: создание новой стадии или правку существующей. */
export type StageDialogState<T> = { mode: "create" } | { mode: "edit"; stage: T };

export interface StageListConfig<T extends StageListItem> {
  /** Заголовок страницы: «Стадии заказов». */
  title: string;
  /** Сколько записей на стадии — от этого зависит, можно ли удалить. */
  countOf: (stage: T) => number;
  /** Формы слова для счётчика: [«заказ», «заказа», «заказов»]. */
  countWords: [string, string, string];
  /** Воронка и её стадии. `pipelineName === null` — воронка не найдена. */
  load: (pipelineId: string) => Promise<{ pipelineName: string | null; stages: T[] }>;
  reorder: (pipelineId: string, items: { id: string; sortOrder: number }[]) => Promise<T[]>;
  remove: (pipelineId: string, stageId: string) => Promise<void>;
  /** Окно создания/правки стадии (у заказов и возвратов — свои поля вида). */
  renderDialog: (props: {
    pipelineId: string;
    state: StageDialogState<T>;
    onClose: () => void;
    onSaved: () => void;
  }) => ReactNode;
}

/** «3 заказа · срок 24 ч» — вторая линия строки стадии. */
export function stageCaption(count: number, words: [string, string, string], slaHours: number | null): string {
  const parts = [`${count} ${pluralRu(count, words)}`];
  if (slaHours) parts.push(`срок ${slaHours} ч`);
  return parts.join(" · ");
}
